import { useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ScheduleHoursDTO, ServiceStatisticsOccupancyDTO } from '../../dto/ServiceStatisticsDTO';
import {
    ALERT_LOW_ATTENDANCE_MISSED_PERCENT, ALERT_MAX_OVERBOOKED_PERCENT, ALERT_MIN_OCCUPANCY_PERCENT, ALERT_MIN_SLOTS,
} from './alertThresholds';
import InfoTip from '../../components/novarium/InfoTip';
import ScheduleHoursSection, { ScheduleHoursStatus } from './ScheduleHoursSection';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import {
    compareOccupancy, formatInt, formatPercent, formatSignedPercent, formatSignedPoints, occupancyOf, relativeChange,
} from './statisticsUtils';

interface OccupancyPanelProps {
    /** Agendas del filtro (la elegida, o todas las de consultorios). */
    agendas: ServiceStatisticsOccupancyDTO[];
    /** La consulta de ocupación falló: se avisa en la pestaña y el resto de la pantalla sigue andando. */
    failed: boolean;
    /** Agenda aplicada como filtro; 0 si no hay. */
    selectedId: number;
    /** Elegir una agenda la aplica como filtro; elegir la aplicada la quita. */
    onToggle: (serviceId: number) => void;
    /** El horario programado contra el real de la agenda elegida; `none` si no hay una agenda de consultorio elegida. */
    schedule: { status: ScheduleHoursStatus; data: ScheduleHoursDTO | null; onRetry: () => void };
    /** En «Mi desempeño» son las agendas del profesional. */
    mode?: StatisticsMode;
}

/** Una agenda necesita al menos estas atenciones (turnos + sobreturnos) para que se la marque por sobreturnos. */
const OVERBOOKED_STATE_MIN_ATTENDED = 10;

/** Cuántas agendas se ven del ranking antes de "Ver todas". */
const RANKING_LIMIT = 12;

interface StatProps {
    label: string;
    value: string;
    hint?: string;
    details: (string | undefined)[];
}

function Stat({ label, value, hint, details }: StatProps) {
    return (
        <div className="nv-ro ss-occ-stat">
            <dt className="ss-kpi__label">
                {label}
                {hint && <InfoTip text={hint} />}
            </dt>
            <dd className="nv-kpi ss-kpi__value">{value}</dd>
            {details.map((text, index) => <dd key={index} className="ss-kpi__detail">{text ?? ' '}</dd>)}
        </div>
    );
}

function pointsChange(intl: IntlShape, current: number | null, previous: number | null): string | undefined {
    return current !== null && previous !== null
        ? intl.formatMessage({ id: 'ServicesStatisticsVsPreviousPoints' }, { value: formatSignedPoints(current, previous) })
        : undefined;
}

/**
 * Pestaña «Agendas»: lugares ocupados, sobreturnos, ausentismo y cancelados; después, con una agenda elegida, su horario
 * programado contra el real (si no, la ayuda para elegirla) y abajo el ranking por agenda (cada fila filtra la pantalla
 * por esa agenda; con una elegida es la única fila). Los totales suman las cantidades y recién después calculan el porcentaje.
 * El porcentaje de ocupación y su variación están en el indicador de arriba y no se repiten acá.
 * TODO(design-system): gráficos. Novarium no define las barras horizontales; la barra es un adorno del porcentaje, que
 * está escrito al lado.
 */
export default function OccupancyPanel({
    agendas, failed, selectedId, onToggle, schedule, mode = 'management',
}: OccupancyPanelProps) {
    const intl = useIntl();
    const [showAll, setShowAll] = useState(false);

    const { totals, comparableCurrent, previous: previousTotals } = useMemo(() => compareOccupancy(agendas), [agendas]);
    const hasOccupancy = !failed && agendas.length > 0 && totals.slots > 0;

    const ranking = useMemo(() => agendas
        .filter(item => item.current.slots > 0)
        .sort((a, b) => occupancyOf(b.current)! - occupancyOf(a.current)!), [agendas]);
    const visible = showAll ? ranking : ranking.slice(0, RANKING_LIMIT);

    const ratio = (count: number, total: number) => (total > 0 ? count / total : 0);
    // Sobreturnos sobre las atenciones de consultorios (turnos regulares + sobreturnos de las mismas agendas de arriba)
    const roomAttentions = totals.booked + totals.overbooked;
    const hasOverbooked = !failed && roomAttentions > 0;
    const comparableAttentions = comparableCurrent.booked + comparableCurrent.overbooked;
    const previousAttentions = previousTotals.booked + previousTotals.overbooked;
    const overbookedChange = comparableAttentions > 0 && previousAttentions > 0
        ? pointsChange(intl, ratio(comparableCurrent.overbooked, comparableAttentions),
            ratio(previousTotals.overbooked, previousAttentions))
        : undefined;
    const cancelledChange = relativeChange(comparableCurrent.cancelled, previousTotals.cancelled);

    return (
        <div className="nv-section">
            <dl className="ss-occ-stats">
                {hasOccupancy && (
                    <Stat
                        label={intl.formatMessage({ id: 'ServicesStatisticsOccupiedSlots' })}
                        hint={intl.formatMessage({ id: modeMessageId(mode, 'OccupiedSlotsHint') })}
                        value={intl.formatMessage({ id: 'ServicesStatisticsOfSlots' },
                            { booked: formatInt(totals.booked), slots: formatInt(totals.slots) })}
                        details={[]}
                    />
                )}
                {hasOverbooked && (
                    <Stat
                        label={intl.formatMessage({ id: 'ServicesStatisticsOverbooked' })}
                        hint={intl.formatMessage({ id: 'ServicesStatisticsOverbookedHint' })}
                        value={formatPercent(ratio(totals.overbooked, roomAttentions), 1)}
                        details={[
                            intl.formatMessage({ id: 'ServicesStatisticsOfRoomAttentions' },
                                { count: formatInt(totals.overbooked), total: formatInt(roomAttentions) }),
                            overbookedChange,
                        ]}
                    />
                )}
                {hasOccupancy && (
                    <>
                        <Stat
                            label={intl.formatMessage({ id: 'ServicesStatisticsMissed' })}
                            hint={intl.formatMessage({ id: 'ServicesStatisticsMissedHint' })}
                            value={totals.missedRate === null ? '—' : formatPercent(totals.missedRate, 1)}
                            details={[
                                intl.formatMessage({ id: 'ServicesStatisticsMissedCount' }, { count: formatInt(totals.missed) }),
                                pointsChange(intl, comparableCurrent.missedRate, previousTotals.missedRate),
                            ]}
                        />
                        <Stat
                            label={intl.formatMessage({ id: 'ServicesStatisticsCancelled' })}
                            value={formatInt(totals.cancelled)}
                            details={[cancelledChange === null ? undefined
                                : intl.formatMessage({ id: 'ServicesStatisticsVsPreviousPercent' },
                                    { value: formatSignedPercent(cancelledChange) })]}
                        />
                    </>
                )}
            </dl>

            {failed && <p className="nv-muted ss-empty">{intl.formatMessage({ id: 'ServicesStatisticsOccupancyFailed' })}</p>}
            {!failed && !hasOccupancy && (
                <p className="nv-muted ss-empty">{intl.formatMessage({ id: 'ServicesStatisticsOccupancyOnlyRooms' })}</p>
            )}

            <ScheduleHoursSection status={schedule.status} data={schedule.data} onRetry={schedule.onRetry} mode={mode} />

            {hasOccupancy && (
                <>
                    <h3 className="nv-subtitle">{intl.formatMessage({ id: modeMessageId(mode, 'OccupancyByAgenda') })}</h3>
                    <ul className="ss-priorities">
                        {visible.map(item => {
                            const agendaRatio = occupancyOf(item.current)!;
                            const agendaOverbooked = item.current.overbooked ?? 0;
                            const saturated = agendaRatio > 1;
                            const { booked, missed, slots } = item.current;
                            const low = slots >= ALERT_MIN_SLOTS && booked * 100 < ALERT_MIN_OCCUPANCY_PERCENT * slots;
                            // «Baja asistencia» reemplaza a «Ocupación baja» si los ausentes son la mitad o más de los reservados
                            // (enteros contra enteros, como el backend: justo 50 % entra)
                            const reserved = booked + missed;
                            const lowAttendance = low && reserved > 0
                                && missed * 100 >= ALERT_LOW_ATTENDANCE_MISSED_PERCENT * reserved;
                            const selected = item.serviceId === selectedId;
                            const detail = intl.formatMessage({ id: 'ServicesStatisticsAgendaDetail' }, {
                                overbooked: formatInt(agendaOverbooked),
                                missed: formatInt(item.current.missed),
                                cancelled: formatInt(item.current.cancelled),
                            });
                            // «con muchos sobreturnos» solo si pasan el máximo de los avisos; unos pocos no lo justifican
                            const attended = item.current.booked + agendaOverbooked;
                            const manyOverbooked = attended >= OVERBOOKED_STATE_MIN_ATTENDED
                                && ratio(agendaOverbooked, attended) * 100 > ALERT_MAX_OVERBOOKED_PERCENT;
                            const stateId = saturated
                                ? (manyOverbooked ? 'ServicesStatisticsStateSaturatedOverbooked' : 'ServicesStatisticsStateSaturated')
                                : manyOverbooked ? 'ServicesStatisticsStateManyOverbooked'
                                    : lowAttendance ? 'ServicesStatisticsStateLowAttendance'
                                        : low ? 'ServicesStatisticsStateLow' : null;
                            const state = stateId ? intl.formatMessage({ id: stateId }) : null;
                            const summary = intl.formatMessage({ id: 'ServicesStatisticsAgendaSummary' }, {
                                booked: formatInt(item.current.booked),
                                slots: formatInt(item.current.slots),
                                percent: formatPercent(agendaRatio),
                            });
                            return (
                                <li key={item.serviceId}>
                                    <button
                                        type="button"
                                        className={`ss-priority ss-rank nv-tip${selected ? ' ss-priority--selected' : ''}`}
                                        aria-pressed={selected}
                                        aria-label={`${item.serviceName}. ${summary}.${state ? ` ${state}.` : ''} ${detail}`}
                                        data-pr-tooltip={detail}
                                        onClick={() => onToggle(item.serviceId)}
                                    >
                                        <span className="ss-priority__name">{item.serviceName}</span>
                                        <span className="ss-priority__track" aria-hidden="true">
                                            <span className="ss-priority__bar" style={{ width: `${Math.min(agendaRatio, 1) * 100}%` }} />
                                        </span>
                                        <span className="ss-priority__value ss-rank__value">{summary}</span>
                                        <span className="ss-rank__state">{state && <Tag className="nv-tag-warn" value={state} />}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {ranking.length > RANKING_LIMIT && (
                        <div className="ss-rank__more">
                            <Button type="button" link
                                label={showAll
                                    ? intl.formatMessage({ id: 'ServicesStatisticsShowFirst' }, { count: RANKING_LIMIT })
                                    : intl.formatMessage({ id: 'ServicesStatisticsShowAll' }, { count: ranking.length })}
                                onClick={() => setShowAll(shown => !shown)} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
